import { useConfirm } from "../context/ConfirmContext";
import { useToast } from "../context/ToastContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import ClientForm, { BLANK_CLIENT_FORM, clientValuesToFormData, type ClientFormValues } from "../components/ClientForm";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DateInput } from "../components/DateInput";
import { formatDate } from "../utils/dateFormat";
import {
  getInvoices, getInvoice, createInvoice, updateInvoice, updateInvoiceStatus, voidInvoice,
  downloadInvoicePdf, downloadInvoiceWhtCertificate, duplicateInvoice, getClients, getCurrencies, createClient, updateClient, deleteClient,
  getTenantCurrency,
  getProducts, createProduct,
} from "../api";

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  draft:     { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
  sent:      { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  paid:      { bg: "#dcfce7", text: "#166534", border: "#86efac" },
  adjusted:  { bg: "#fef3c7", text: "#92400e", border: "#fde68a" },
  cancelled: { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  voided:    { bg: "#f8f8f8", text: "#94a3b8", border: "#e2e8f0" },
};

const TAX_SCHEMES: Record<string, { label: string; taxes: { name: string; rate: string }[]; wht_label: string; wht_options: { label: string; rate: string }[] }> = {
  custom:     { label: "Custom",                taxes: [],                                                           wht_label: "TDS / WHT", wht_options: [] },
  thailand:   { label: "🇹🇭 Thailand",           taxes: [{ name: "VAT", rate: "7" }],                                wht_label: "WHT",       wht_options: [{ label: "Professional Services", rate: "3" }, { label: "Advertising", rate: "2" }, { label: "Rent", rate: "5" }, { label: "Transport", rate: "1" }] },
  india:      { label: "🇮🇳 India",              taxes: [{ name: "CGST", rate: "9" }, { name: "SGST", rate: "9" }],  wht_label: "TDS",       wht_options: [{ label: "Professional/Technical (194J)", rate: "10" }, { label: "Contractor (194C)", rate: "1" }, { label: "Rent (194I)", rate: "10" }, { label: "Commission (194H)", rate: "5" }] },
  singapore:  { label: "🇸🇬 Singapore",          taxes: [{ name: "GST", rate: "9" }],                                wht_label: "WHT",       wht_options: [{ label: "Professional Services", rate: "15" }, { label: "Royalties", rate: "10" }] },
  uk:         { label: "🇬🇧 United Kingdom",      taxes: [{ name: "VAT", rate: "20" }],                               wht_label: "WHT",       wht_options: [] },
  uae:        { label: "🇦🇪 UAE",                 taxes: [{ name: "VAT", rate: "5" }],                                wht_label: "WHT",       wht_options: [] },
  australia:  { label: "🇦🇺 Australia",           taxes: [{ name: "GST", rate: "10" }],                               wht_label: "WHT",       wht_options: [] },
  zero_rated: { label: "Zero-rated / Export",    taxes: [{ name: "VAT (Zero-rated)", rate: "0" }],                    wht_label: "WHT",       wht_options: [] },
};

function getDocFormErrors(f: { issue_date: string; line_items: { description: string }[] }) {
  const errors: { issue_date?: string; line_items?: string } = {};
  if (!f.issue_date) errors.issue_date = "Issue date is required";
  if (!f.line_items.some(item => item.description.trim())) errors.line_items = "Add at least one line item with a description";
  return errors;
}

// The submit button is disabled while required fields are missing, so a plain click
// can't trigger a submit attempt. Enter / Cmd+Enter still reaches the form though —
// surface the field-level errors then, instead of silently doing nothing.
function handleDocFormKeyDown(e: React.KeyboardEvent<HTMLFormElement>, hasErrors: boolean, setTriedSubmit: (v: boolean) => void) {
  if (e.key !== "Enter") return;
  const tag = (e.target as HTMLElement).tagName;
  // Buttons (Add Item, Add Tax Row, remove-row ✕, etc.) handle their own Enter/click —
  // don't hijack their keydown just because the form happens to be invalid.
  if (tag === "BUTTON") return;
  const isSubmitShortcut = e.metaKey || e.ctrlKey || tag !== "TEXTAREA";
  if (isSubmitShortcut && hasErrors) {
    e.preventDefault();
    setTriedSubmit(true);
  }
}

export default function InvoicesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"invoices" | "clients">("invoices");
  const [statusFilter, setStatusFilter] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [editFetching, setEditFetching] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<any | null>(null);
  const [invTriedSubmit, setInvTriedSubmit] = useState(false);

  // ── Product catalog state: inline "create on the fly" from a line item only —
  // full product management lives on the standalone Products page ──────────
  // Modal for creating a product on-the-fly from a line item
  const [productModal, setProductModal] = useState<{
    name: string; description: string; unit_price: string;
    lineItemType: "inv" | null; lineItemIdx: number | null;
  } | null>(null);
  // Autocomplete: which line item (type + index) has the dropdown open
  const [acOpen, setAcOpen] = useState<{ type: "inv"; idx: number } | null>(null);

  // ── Client management state ──────────────────────────────────────────────
  const [clientForm, setClientForm] = useState<ClientFormValues>(BLANK_CLIENT_FORM);
  const [editingClient, setEditingClient] = useState<any | null>(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [invSort, setInvSort] = useState<{ field: string; dir: "asc" | "desc" }>({ field: "issue_date", dir: "desc" });
  const [invSearch, setInvSearch] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const addDays = (dateStr: string, days: number): string => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split("T")[0];
  };
  const [form, setForm] = useState({
    client_id: "",
    currency: "",
    currency_symbol: "",
    issue_date: today,
    due_date: "",
    credit_days: "",
    project_name: "",
    tax_scheme: "",
    document_type: "invoice",
    taxes: [{ name: "", rate: "" }] as { name: string; rate: string }[],
    tds_rate: "",
    tds_label: "TDS / WHT",
    discount: "0",
    notes: "",
    line_items: [{ description: "", quantity: "1", unit_price: "", amount: "", detail: "" }] as {
      description: string; quantity: string; unit_price: string; amount: string; detail: string;
    }[],
  });

  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: getClients });
  const { data: allProducts = [] } = useQuery({ queryKey: ["products"], queryFn: () => getProducts() });
  const { data: currencies = [] } = useQuery({ queryKey: ["currencies"], queryFn: getCurrencies });
  const { data: tenantCurrency } = useQuery({ queryKey: ["tenantCurrency"], queryFn: getTenantCurrency });
  const tc = tenantCurrency as { currency: string; currency_symbol: string; tax_label: string; tax_rate: string } | undefined;
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["invoices", statusFilter, overdueOnly],
    queryFn: () => getInvoices({
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(overdueOnly ? { overdue: true } : {}),
    }),
  });

  const createMut = useMutation({
    mutationFn: createInvoice,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      setShowForm(false);
      resetForm();
      toast("Invoice created successfully", "success");
    },
    onError: () => toast("Failed to create invoice", "error"),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => updateInvoiceStatus(id, status),
    onSuccess: (_, { status }) => { qc.invalidateQueries({ queryKey: ["invoices"] }); toast(`Invoice marked as ${status}`, "success"); },
    onError: () => toast("Failed to update invoice status", "error"),
  });

  const voidMut = useMutation({
    mutationFn: voidInvoice,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); toast("Invoice voided", "success"); },
    onError: () => toast("Failed to void invoice", "error"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: object }) => updateInvoice(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      setShowForm(false);
      setEditingInvoice(null);
      resetForm();
      toast("Invoice updated", "success");
    },
    onError: () => toast("Failed to update invoice", "error"),
  });

  const duplicateMut = useMutation({
    mutationFn: duplicateInvoice,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); toast("Invoice duplicated", "success"); },
    onError: () => toast("Failed to duplicate invoice", "error"),
  });

  // ── Client mutations ─────────────────────────────────────────────────────
  const createClientMut = useMutation({
    mutationFn: (fd: FormData) => createClient(fd),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clients"] }); setShowClientForm(false); setClientForm(BLANK_CLIENT_FORM); },
    onError: (err: any) => toast(err?.response?.data?.detail || "Failed to save client", "error"),
  });
  const updateClientMut = useMutation({
    mutationFn: ({ id, fd }: { id: number; fd: FormData }) => updateClient(id, fd),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clients"] }); setEditingClient(null); setClientForm(BLANK_CLIENT_FORM); },
    onError: (err: any) => toast(err?.response?.data?.detail || "Failed to save client", "error"),
  });
  const deleteClientMut = useMutation({
    mutationFn: deleteClient,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients"] }),
    onError: (err: any) => toast(err?.response?.data?.detail || "Failed to delete client", "error"),
  });

  // ── Product mutation: inline "create on the fly" from a line item ──────────
  const createProductMut = useMutation({
    mutationFn: (data: object) => createProduct(data),
    onSuccess: (p: any) => {
      qc.invalidateQueries({ queryKey: ["products"] });
      if (productModal?.lineItemType === "inv" && productModal.lineItemIdx !== null) {
        _fillInvItem(productModal.lineItemIdx, p);
      }
      setProductModal(null);
    },
  });

  const _fillInvItem = (idx: number, p: any) => {
    setForm(f => {
      const items = [...f.line_items];
      const qty = parseFloat(items[idx].quantity) || 1;
      items[idx] = { ...items[idx], description: p.name, unit_price: String(p.unit_price), amount: (qty * p.unit_price).toFixed(2) };
      return { ...f, line_items: items };
    });
    setAcOpen(null);
  };
  const resetForm = () => {
    setInvTriedSubmit(false);
    setForm({
    client_id: "", currency: tc?.currency || "", currency_symbol: tc?.currency_symbol || "",
    issue_date: today, due_date: "", credit_days: "", project_name: "", tax_scheme: "",
    document_type: "invoice",
    taxes: [{ name: "", rate: "" }], tds_rate: "", tds_label: "TDS / WHT",
    discount: "0", notes: "",
    line_items: [{ description: "", quantity: "1", unit_price: "", amount: "", detail: "" }],
    });
  };

  const applyTaxScheme = (key: string) => {
    const s = TAX_SCHEMES[key];
    if (!s) return;
    setForm(f => ({
      ...f,
      tax_scheme: key,
      taxes: s.taxes.length > 0 ? s.taxes.map(t => ({ ...t })) : [{ name: "", rate: "" }],
      tds_label: s.wht_label,
    }));
  };

  // Auto-apply client's tax scheme when client_id changes in invoice form
  useEffect(() => {
    if (!form.client_id) return;
    const c = (clients as any[]).find((x: any) => String(x.id) === form.client_id);
    if (c?.tax_scheme && TAX_SCHEMES[c.tax_scheme]) applyTaxScheme(c.tax_scheme);
  }, [form.client_id]); // eslint-disable-line

  // When tenant currency loads, backfill any open new-form that still has empty currency
  useEffect(() => {
    if (!tc) return;
    if (showForm && !editingInvoice) {
      setForm(f => ({ ...f,
        currency: f.currency || tc.currency,
        currency_symbol: f.currency_symbol || tc.currency_symbol,
        taxes: f.taxes.map(t => ({ ...t, name: t.name || tc.tax_label || "" })),
      }));
    }
  }, [tc]);

  const onCurrencyChange = (code: string) => {
    const found = currencies.find((c: any) => c.code === code);
    setForm(f => ({ ...f, currency: code, currency_symbol: found?.symbol || "" }));
  };

  const updateLineItem = (i: number, field: string, value: string) => {
    setForm(f => {
      const items = [...f.line_items];
      items[i] = { ...items[i], [field]: value };
      if (field === "quantity" || field === "unit_price") {
        const qty = parseFloat(field === "quantity" ? value : items[i].quantity) || 0;
        const price = parseFloat(field === "unit_price" ? value : items[i].unit_price) || 0;
        items[i].amount = (qty * price).toFixed(2);
      }
      return { ...f, line_items: items };
    });
  };

  const addLineItem = () =>
    setForm(f => ({ ...f, line_items: [...f.line_items, { description: "", quantity: "1", unit_price: "", amount: "", detail: "" }] }));

  const removeLineItem = (i: number) =>
    setForm(f => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const calcSubtotal = () => form.line_items.reduce((s, item) => s + (parseFloat(item.amount) || 0), 0);
  const calcDiscount = () => parseFloat(form.discount) || 0;
  const calcTaxAmount = () => form.taxes.reduce((s, t) => {
    const rate = parseFloat(t.rate) || 0;
    return s + ((calcSubtotal() - calcDiscount()) * rate / 100);
  }, 0);
  const calcTotal = () => calcSubtotal() - calcDiscount() + calcTaxAmount();
  const calcTdsAmount = () => calcSubtotal() * (parseFloat(form.tds_rate) || 0) / 100;
  const calcNetPayable = () => calcTotal() - calcTdsAmount();

  const invoiceErrors = getDocFormErrors(form);
  const invoiceHasErrors = Object.keys(invoiceErrors).length > 0;
  const invoiceSubmitDisabled = createMut.isPending || updateMut.isPending || invoiceHasErrors;

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.keys(invoiceErrors).length > 0) {
      setInvTriedSubmit(true);
      return;
    }
    const activeTaxes = form.taxes
      .filter(t => t.name.trim() || parseFloat(t.rate) > 0)
      .map(t => ({
        name: t.name || "Tax",
        rate: parseFloat(t.rate) || 0,
        amount: parseFloat(((calcSubtotal() - calcDiscount()) * (parseFloat(t.rate) || 0) / 100).toFixed(2)),
      }));
    const payload = {
      client_id: form.client_id ? parseInt(form.client_id) : null,
      currency: form.currency || undefined,
      currency_symbol: form.currency_symbol || undefined,
      issue_date: form.issue_date,
      due_date: form.due_date || undefined,
      project_name: form.project_name || undefined,
      tax_scheme: form.tax_scheme || undefined,
      document_type: form.document_type || "invoice",
      taxes: activeTaxes,
      tds_rate: parseFloat(form.tds_rate) || 0,
      tds_label: form.tds_label || undefined,
      discount: calcDiscount(),
      notes: form.notes,
      line_items: form.line_items.map(item => ({
        description: item.description,
        quantity: parseFloat(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        amount: parseFloat(item.amount) || 0,
        detail: item.detail || "",
      })),
    };
    if (editingInvoice) {
      updateMut.mutate({ id: editingInvoice.id, data: payload });
    } else {
      createMut.mutate(payload);
    }
  };

  const handleDownload = async (inv: any) => {
    setDownloading(inv.id);
    try {
      const { blob, filename } = await downloadInvoicePdf(inv.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Failed to download PDF", "error");
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadWhtCertificate = async (inv: any) => {
    setDownloading(inv.id);
    try {
      const { blob, filename } = await downloadInvoiceWhtCertificate(inv.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Failed to download WHT certificate", "error");
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadFromForm = async () => {
    if (!editingInvoice) return;
    setDownloading(editingInvoice.id);
    try {
      const activeTaxes = form.taxes
        .filter(t => t.name.trim() || parseFloat(t.rate) > 0)
        .map(t => ({
          name: t.name || "Tax",
          rate: parseFloat(t.rate) || 0,
          amount: parseFloat(((calcSubtotal() - calcDiscount()) * (parseFloat(t.rate) || 0) / 100).toFixed(2)),
        }));
      const payload = {
        client_id: form.client_id ? parseInt(form.client_id) : null,
        currency: form.currency || undefined,
        currency_symbol: form.currency_symbol || undefined,
        issue_date: form.issue_date,
        due_date: form.due_date || undefined,
        project_name: form.project_name || undefined,
        tax_scheme: form.tax_scheme || undefined,
        document_type: form.document_type || "invoice",
        taxes: activeTaxes,
        tds_rate: parseFloat(form.tds_rate) || 0,
        tds_label: form.tds_label || undefined,
        discount: calcDiscount(),
        notes: form.notes,
        line_items: form.line_items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          amount: parseFloat(item.amount) || 0,
          detail: item.detail || "",
        })),
      };
      await updateInvoice(editingInvoice.id, payload);
      qc.invalidateQueries({ queryKey: ["invoices"] });
      const { blob, filename } = await downloadInvoicePdf(editingInvoice.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Failed to download PDF", "error");
    } finally {
      setDownloading(null);
    }
  };

  const handleClientSubmit = (values: ClientFormValues) => {
    const fd = clientValuesToFormData(values);
    if (editingClient) {
      updateClientMut.mutate({ id: editingClient.id, fd });
    } else {
      createClientMut.mutate(fd);
    }
  };

  const startEditClient = (c: any) => {
    setEditingClient(c);
    setClientForm({ name: c.name || "", contact_email: c.contact_email || "", contact_person: c.contact_person || "", address: c.address || "", tax_id: c.tax_id || "", tax_label: c.tax_label || "", tax_scheme: c.tax_scheme || "", branch_number: c.branch_number || "" });
    setShowClientForm(true);
  };

  const cancelClientForm = () => { setShowClientForm(false); setEditingClient(null); setClientForm(BLANK_CLIENT_FORM); };

  const startEditInvoice = async (inv: any) => {
    setEditFetching(true);
    const full = await getInvoice(inv.id).finally(() => setEditFetching(false));
    let taxes: { name: string; rate: string }[] = [{ name: "", rate: "" }];
    if (full.taxes && full.taxes.length > 0) {
      taxes = full.taxes.map((t: any) => ({ name: String(t.name || ""), rate: String(t.rate || "") }));
    } else if (full.tax_rate) {
      taxes = [{ name: tc?.tax_label || "Tax", rate: String(full.tax_rate) }];
    }
    setForm({
      client_id: String(full.client_id || ""),
      currency: full.currency || "",
      currency_symbol: full.currency_symbol || "",
      issue_date: full.issue_date,
      due_date: full.due_date || "",
      project_name: full.project_name || "",
      tax_scheme: full.tax_scheme || "",
      document_type: full.document_type || "invoice",
      taxes,
      tds_rate: String(full.tds_rate || ""),
      tds_label: full.tds_label || "TDS / WHT",
      discount: String(full.discount || "0"),
      notes: full.notes || "",
      line_items: (full.line_items || []).map((item: any) => ({
        description: String(item.description || ""),
        quantity: String(item.quantity || "1"),
        unit_price: String(item.unit_price || ""),
        amount: String(item.amount || ""),
        detail: String(item.detail || ""),
      })),
    });
    setEditingInvoice(full);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const sym = form.currency_symbol || tc?.currency_symbol || "$";

  const sortRows = (rows: any[], field: string, dir: "asc" | "desc") =>
    [...rows].sort((a, b) => {
      const av = a[field] ?? "";
      const bv = b[field] ?? "";
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return dir === "asc" ? cmp : -cmp;
    });

  const filteredInvoices = sortRows(
    (invoices as any[]).filter(inv => {
      if (!invSearch) return true;
      const s = invSearch.toLowerCase();
      return (inv.invoice_number || "").toLowerCase().includes(s) ||
             (inv.client_name || "").toLowerCase().includes(s);
    }),
    invSort.field, invSort.dir
  );

  // Selected client preview in invoice form
  const selectedClient = form.client_id ? clients.find((c: any) => c.id === parseInt(form.client_id)) : null;

  return (
    <>
    <div>
      <LoadingOverlay active={
        duplicateMut.isPending || statusMut.isPending || voidMut.isPending || editFetching ||
        downloading !== null ||
        createClientMut.isPending || updateClientMut.isPending || deleteClientMut.isPending ||
        createProductMut.isPending
      } />
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0, color: "#0f172a", fontSize: 24, fontWeight: 700 }}>Invoices</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 14 }}>Generate and manage client invoices</p>
        </div>
        {tab === "invoices" && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => exportInvoicesCsv(filteredInvoices)} style={outlineBtnStyle}>↓ Export CSV</button>
            <button onClick={() => { setShowForm(f => !f); setEditingInvoice(null); resetForm(); }} style={primaryBtnStyle}>
              {showForm ? "Cancel" : "+ New Invoice"}
            </button>
          </div>
        )}
        {tab === "clients" && (
          <button onClick={() => { setShowClientForm(!showClientForm); setEditingClient(null); setClientForm(BLANK_CLIENT_FORM); }} style={primaryBtnStyle}>
            {showClientForm ? "Cancel" : "+ Add Client"}
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div className="tab-pill-bar" style={{ marginBottom: 24 }}>
        {(["invoices", "clients"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={tab === t ? "active" : ""}>
            {t === "invoices" ? "Invoices" : "Clients"}
          </button>
        ))}
      </div>

      {/* ── Clients Tab ── */}
      {tab === "clients" && (
        <div>
          {/* Client Form */}
          {showClientForm && (
            <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: "0 0 16px", color: "#1e3a8a", fontSize: 16 }}>
                {editingClient ? "Edit Client" : "New Client"}
              </h3>
              <ClientForm
                value={clientForm}
                onChange={setClientForm}
                onSubmit={handleClientSubmit}
                onCancel={cancelClientForm}
                existingClients={clients as any[]}
                excludeId={editingClient?.id ?? null}
                isEditing={!!editingClient}
                submitting={createClientMut.isPending || updateClientMut.isPending}
                taxSchemeOptions={[
                  { value: "", label: "Custom (no preset)" },
                  ...Object.entries(TAX_SCHEMES).filter(([k]) => k !== "custom").map(([k, s]) => ({ value: k, label: s.label })),
                ]}
                styles={{ label: labelStyle, input: inputStyle, submitButton: primaryBtnStyle, cancelButton: outlineBtnStyle }}
              />
            </div>
          )}

          {/* Clients List */}
          {clients.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
              <div style={{ fontSize: 48 }}>🏢</div>
              <div style={{ fontSize: 15, marginTop: 12 }}>No clients yet. Add your first client.</div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {clients.map((c: any) => (
                <div key={c.id} style={{
                  background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
                  padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b", marginBottom: 4 }}>{c.name}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", fontSize: 13, color: "#64748b" }}>
                      {c.contact_person && <span>👤 {c.contact_person}</span>}
                      {c.contact_email && <span>✉ {c.contact_email}</span>}
                      {c.tax_id && <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>{c.tax_label || "Tax ID"}: {c.tax_id}</span>}
                      {c.tax_scheme && <span style={{ background: "#f0fdf4", color: "#166534", border: "1px solid #86efac", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>{TAX_SCHEMES[c.tax_scheme]?.label || c.tax_scheme}</span>}
                      {c.branch_number && <span style={{ background: "#fefce8", color: "#854d0e", border: "1px solid #fde047", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>Branch: {c.branch_number}</span>}
                    </div>
                    {c.address && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>{c.address}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => startEditClient(c)} style={outlineBtnStyle}>Edit</button>
                    <AsyncButton onClick={async () => { if (await confirm(`Delete client "${c.name}"?`, { confirmLabel: "Delete" })) deleteClientMut.mutate(c.id); }}
                      style={{ ...outlineBtnStyle, color: "#dc2626", borderColor: "#fca5a5" }}>Delete</AsyncButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Invoices Tab ── */}
      {tab === "invoices" && (
        <>
          {/* ── Create Form ── */}
          {showForm && (
            <div style={{ position: "fixed", inset: 0, background: "#f1f5f9", zIndex: 1000, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <LoadingOverlay active={createMut.isPending || updateMut.isPending || downloading !== null} />
              {/* ── Top bar ── */}
              <div style={{ background: "white", borderBottom: "1px solid #e2e8f0", padding: "0 24px", height: 60, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button onClick={() => { setShowForm(false); setEditingInvoice(null); resetForm(); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", fontSize: 14, fontWeight: 600, padding: "6px 10px", borderRadius: 6, display: "flex", alignItems: "center", gap: 6 }}
                    onMouseEnter={e => (e.currentTarget.style.background = "#f1f5f9")}
                    onMouseLeave={e => (e.currentTarget.style.background = "none")}>
                    ← Close
                  </button>
                  <div style={{ width: 1, height: 24, background: "#e2e8f0" }} />
                  <span style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
                    {editingInvoice ? editingInvoice.invoice_number : "New Invoice"}
                  </span>
                  {editingInvoice && (() => { const sc = STATUS_COLORS[editingInvoice.status] || STATUS_COLORS.draft; return (
                    <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: "capitalize" }}>{editingInvoice.status}</span>
                  ); })()}
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  {editingInvoice && (editingInvoice.status === "sent" || editingInvoice.status === "paid") && (
                    <>
                      <button type="button" onClick={() => navigate("/debit-credit-notes", { state: { prefillInvoiceId: editingInvoice.id, prefillType: "credit_note" } })} style={outlineBtnStyle}>
                        Create Credit Note
                      </button>
                      <button type="button" onClick={() => navigate("/debit-credit-notes", { state: { prefillInvoiceId: editingInvoice.id, prefillType: "debit_note" } })} style={outlineBtnStyle}>
                        Create Debit Note
                      </button>
                    </>
                  )}
                  {editingInvoice && (
                    <AsyncButton onClick={handleDownloadFromForm} style={{ ...outlineBtnStyle, display: "flex", alignItems: "center", gap: 6 }}>
                      {downloading === editingInvoice.id ? "Saving & Downloading…" : "⬇ Download PDF"}
                    </AsyncButton>
                  )}
                  <button type="submit" form="invoice-form" disabled={invoiceSubmitDisabled}
                    title={invoiceHasErrors ? "Add at least one line item and an issue date to continue" : undefined}
                    style={{
                      ...primaryBtnStyle, padding: "8px 22px",
                      opacity: invoiceSubmitDisabled ? 0.5 : 1,
                      cursor: invoiceSubmitDisabled ? "not-allowed" : "pointer",
                    }}>
                    {createMut.isPending || updateMut.isPending ? "Saving…" : editingInvoice ? "Save Changes" : "Create Invoice"}
                  </button>
                </div>
              </div>

              {/* ── Scrollable content ── */}
              <div style={{ flex: 1, overflowY: "auto", padding: "28px 24px" }}>
                <div style={{ maxWidth: 960, margin: "0 auto", background: "white", borderRadius: 12, padding: "28px 32px", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
              <form id="invoice-form" onSubmit={handleCreate} onKeyDown={e => handleDocFormKeyDown(e, invoiceHasErrors, setInvTriedSubmit)}>
                {/* Header fields */}
                <div className="grid-3col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
                  <div>
                    <label style={labelStyle}>Client</label>
                    <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} style={inputStyle}>
                      <option value="">No client / Internal</option>
                      {clients.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Currency</label>
                    <select value={form.currency} onChange={e => onCurrencyChange(e.target.value)} style={inputStyle}>
                      <option value="">Use tenant default</option>
                      {currencies.map((c: any) => (
                        <option key={c.code} value={c.code}>{c.name} ({c.symbol})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Issue Date <span style={requiredMarkStyle}>*</span></label>
                    <DateInput value={form.issue_date}
                      onChange={e => {
                        const d = e.target.value;
                        setForm(f => ({
                          ...f,
                          issue_date: d,
                          due_date: f.credit_days && d ? addDays(d, parseInt(f.credit_days)) : f.due_date,
                        }));
                      }} style={inputStyle} required />
                    {invTriedSubmit && invoiceErrors.issue_date && <div style={errorTextStyle}>{invoiceErrors.issue_date}</div>}
                  </div>
                  <div>
                    <label style={labelStyle}>Due Date</label>
                    <DateInput value={form.due_date} min={form.issue_date || today}
                      onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Credit Days</label>
                    <input type="number" min="0" placeholder="e.g. 30" value={form.credit_days}
                      onChange={e => {
                        const days = e.target.value;
                        setForm(f => ({
                          ...f,
                          credit_days: days,
                          due_date: days && f.issue_date ? addDays(f.issue_date, parseInt(days)) : f.due_date,
                        }));
                      }} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Project Name</label>
                    <input placeholder="e.g. Software Analyst" value={form.project_name}
                      onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Discount ({sym})</label>
                    <input type="number" value={form.discount}
                      onChange={e => setForm(f => ({ ...f, discount: e.target.value }))}
                      style={inputStyle} min={0} step="0.01" />
                  </div>
                </div>

                {/* Tax Scheme */}
                <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, color: "#475569", whiteSpace: "nowrap" }}>Tax Scheme</label>
                  <select value={form.tax_scheme} onChange={e => applyTaxScheme(e.target.value)} style={{ ...inputStyle, maxWidth: 240 }}>
                    <option value="">Custom</option>
                    {Object.entries(TAX_SCHEMES).filter(([k]) => k !== "custom").map(([k, s]) => (
                      <option key={k} value={k}>{s.label}</option>
                    ))}
                  </select>
                  {form.tax_scheme && <button type="button" onClick={() => setForm(f => ({ ...f, tax_scheme: "" }))} style={{ fontSize: 12, color: "#94a3b8", background: "none", border: "none", cursor: "pointer" }}>✕ Clear</button>}
                </div>

                {/* Document Type — Thailand tax invoice labeling */}
                {form.tax_scheme === "thailand" && (
                  <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, color: "#475569", whiteSpace: "nowrap" }}>Document Type</label>
                    <select value={form.document_type} onChange={e => setForm(f => ({ ...f, document_type: e.target.value }))} style={{ ...inputStyle, maxWidth: 280 }}>
                      <option value="invoice">Generic Invoice</option>
                      <option value="tax_invoice">Tax Invoice</option>
                      <option value="abbreviated_tax_invoice">Abbreviated Tax Invoice</option>
                    </select>
                  </div>
                )}

                {/* Tax Rows */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, color: "#475569" }}>Tax / GST / VAT</label>
                    <button type="button" onClick={() => setForm(f => ({ ...f, taxes: [...f.taxes, { name: "", rate: "" }] }))} style={outlineBtnStyle}>+ Add Tax Row</button>
                  </div>
                  {form.taxes.map((tax, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 8, marginBottom: 8 }}>
                      <input placeholder="e.g. GST / CGST / IGST / VAT" value={tax.name}
                        onChange={e => setForm(f => { const taxes = [...f.taxes]; taxes[i] = { ...taxes[i], name: e.target.value }; return { ...f, taxes }; })}
                        style={inputStyle} />
                      <input type="number" placeholder="Rate %" value={tax.rate} min={0} max={100} step="0.01"
                        onChange={e => setForm(f => { const taxes = [...f.taxes]; taxes[i] = { ...taxes[i], rate: e.target.value }; return { ...f, taxes }; })}
                        style={inputStyle} />
                      {form.taxes.length > 1 && (
                        <button type="button" onClick={() => setForm(f => ({ ...f, taxes: f.taxes.filter((_, idx) => idx !== i) }))} style={{
                          background: "none", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 6, cursor: "pointer", padding: "0 10px",
                        }}>✕</button>
                      )}
                    </div>
                  ))}
                </div>

                {/* TDS / WHT */}
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>{TAX_SCHEMES[form.tax_scheme]?.wht_label || "TDS / WHT"} Deduction (% on subtotal) — leave 0 if not applicable</label>
                  {(TAX_SCHEMES[form.tax_scheme]?.wht_options || []).length > 0 && (
                    <select onChange={e => { const o = TAX_SCHEMES[form.tax_scheme].wht_options.find(x => x.label === e.target.value); if (o) setForm(f => ({ ...f, tds_label: o.label, tds_rate: o.rate })); }} style={{ ...inputStyle, marginBottom: 6, color: "#64748b" }}>
                      <option value="">— Select {TAX_SCHEMES[form.tax_scheme]?.wht_label} category —</option>
                      {TAX_SCHEMES[form.tax_scheme].wht_options.map(o => <option key={o.label} value={o.label}>{o.label} ({o.rate}%)</option>)}
                    </select>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8 }}>
                    <input placeholder="Label e.g. TDS / WHT" value={form.tds_label}
                      onChange={e => setForm(f => ({ ...f, tds_label: e.target.value }))} style={inputStyle} />
                    <input type="number" placeholder="0" value={form.tds_rate} min={0} max={100} step="0.01"
                      onChange={e => setForm(f => ({ ...f, tds_rate: e.target.value }))} style={inputStyle} />
                  </div>
                </div>

                {/* Client info preview */}
                {selectedClient && (
                  <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#1d4ed8" }}>
                    <strong>{selectedClient.name}</strong>
                    {selectedClient.contact_person && <span style={{ marginLeft: 12 }}>👤 {selectedClient.contact_person}</span>}
                    {selectedClient.contact_email && <span style={{ marginLeft: 12 }}>✉ {selectedClient.contact_email}</span>}
                    {selectedClient.tax_id && <span style={{ marginLeft: 12 }}>Tax ID: {selectedClient.tax_id}</span>}
                    {selectedClient.address && <div style={{ marginTop: 4, color: "#3b82f6" }}>{selectedClient.address}</div>}
                  </div>
                )}

                {/* Line Items */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, color: "#475569" }}>Line Items <span style={requiredMarkStyle}>*</span></label>
                    <button type="button" onClick={addLineItem} style={outlineBtnStyle}>+ Add Item</button>
                  </div>
                  {invTriedSubmit && invoiceErrors.line_items && <div style={errorTextStyle}>{invoiceErrors.line_items}</div>}
                  <div className="table-wrap">
                  <div style={{ display: "grid", gridTemplateColumns: "28px 3fr 1fr 1.5fr 1.5fr auto", gap: 8, marginBottom: 6, minWidth: 480 }}>
                    {["#", "Description", "Qty", `Unit Price (${sym})`, `Amount (${sym})`, ""].map(h => (
                      <div key={h} style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textAlign: h === "#" ? "center" : "left" }}>{h}</div>
                    ))}
                  </div>
                  {form.line_items.map((item, i) => (
                    <div key={i} style={{ marginBottom: 8, minWidth: 480 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "28px 3fr 1fr 1.5fr 1.5fr auto", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#94a3b8", paddingTop: 2 }}>{i + 1}</div>
                      <div style={{ position: "relative" }}>
                        <input placeholder="Description (type to search products…)" value={item.description}
                          onChange={e => { updateLineItem(i, "description", e.target.value); setAcOpen({ type: "inv", idx: i }); }}
                          onFocus={() => setAcOpen({ type: "inv", idx: i })}
                          onBlur={() => setTimeout(() => setAcOpen(null), 160)}
                          style={inputStyle} />
                        {acOpen?.type === "inv" && acOpen.idx === i && (() => {
                          const q = item.description.toLowerCase();
                          const hits = (allProducts as any[]).filter((p: any) => !q || p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q)).slice(0, 7);
                          if (!hits.length && !q) return null;
                          return (
                            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, background: "white", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", marginTop: 2 }}>
                              {hits.map((p: any) => (
                                <button key={p.id} type="button" onMouseDown={() => _fillInvItem(i, p)}
                                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "8px 12px", background: "none", border: "none", borderBottom: "1px solid #f1f5f9", cursor: "pointer", textAlign: "left" }}
                                  onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                                  onMouseLeave={e => (e.currentTarget.style.background = "none")}>
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{p.name}</div>
                                    {p.description && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{p.description}</div>}
                                  </div>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: "#0f766e", marginLeft: 12, whiteSpace: "nowrap" }}>{p.unit_price.toLocaleString("en", { minimumFractionDigits: 2 })}</span>
                                </button>
                              ))}
                              <button type="button" onMouseDown={() => { setProductModal({ name: item.description, description: "", unit_price: "", lineItemType: "inv", lineItemIdx: i }); setAcOpen(null); }}
                                style={{ display: "block", width: "100%", padding: "8px 12px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: 12, color: "#1d4ed8", fontWeight: 600 }}
                                onMouseEnter={e => (e.currentTarget.style.background = "#eff6ff")}
                                onMouseLeave={e => (e.currentTarget.style.background = "none")}>
                                + Save "{item.description || "…"}" as new product
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                      <input type="number" placeholder="1" value={item.quantity} min={0} step="0.01"
                        onChange={e => updateLineItem(i, "quantity", e.target.value)} style={inputStyle} />
                      <input type="number" placeholder="0.00" value={item.unit_price} min={0} step="0.01"
                        onChange={e => updateLineItem(i, "unit_price", e.target.value)} style={inputStyle} />
                      <input type="number" placeholder="0.00" value={item.amount} min={0} step="0.01"
                        onChange={e => updateLineItem(i, "amount", e.target.value)} style={inputStyle} />
                      <button type="button" onClick={() => removeLineItem(i)} style={{
                        background: "none", border: "1px solid #fca5a5", color: "#dc2626",
                        borderRadius: 6, cursor: "pointer", padding: "0 10px",
                      }}>✕</button>
                    </div>
                    <input placeholder="Detail / notes (e.g. 1 July – 30 Sep 2026, ID OS188)…" value={item.detail || ""}
                      onChange={e => updateLineItem(i, "detail", e.target.value)}
                      style={{ ...inputStyle, marginTop: 3, marginLeft: 36, width: "calc(100% - 36px)", fontSize: 12, color: "#64748b" }} />
                    </div>
                  ))}
                  </div>
                </div>

                {/* Totals summary */}
                <div style={{ background: "#f8fafc", borderRadius: 8, padding: 14, marginBottom: 14 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      Subtotal: <strong>{sym}{calcSubtotal().toFixed(2)}</strong>
                    </div>
                    {calcDiscount() > 0 && (
                      <div style={{ fontSize: 13, color: "#64748b" }}>
                        Discount: <strong>-{sym}{calcDiscount().toFixed(2)}</strong>
                      </div>
                    )}
                    {form.taxes.filter(t => parseFloat(t.rate) > 0).map((t, i) => (
                      <div key={i} style={{ fontSize: 13, color: "#64748b" }}>
                        {t.name || "Tax"} ({t.rate}%): <strong>{sym}{((calcSubtotal() - calcDiscount()) * (parseFloat(t.rate) || 0) / 100).toFixed(2)}</strong>
                      </div>
                    ))}
                    {calcTdsAmount() > 0 ? (
                      <>
                        <div style={{ fontSize: 15, color: "#1e3a8a", fontWeight: 700, marginTop: 4, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                          Gross Total: {sym}{calcTotal().toFixed(2)}
                        </div>
                        <div style={{ fontSize: 13, color: "#dc2626" }}>
                          {form.tds_label || "TDS / WHT"} ({form.tds_rate}%): <strong>-{sym}{calcTdsAmount().toFixed(2)}</strong>
                        </div>
                        <div style={{ fontSize: 16, color: "#0f766e", fontWeight: 700, marginTop: 4, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                          Net Payable: {sym}{calcNetPayable().toFixed(2)}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 16, color: "#1e3a8a", fontWeight: 700, marginTop: 4, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                        Total: {sym}{calcTotal().toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Notes / Terms</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Payment terms, notes..." rows={2}
                    style={{ ...inputStyle, resize: "vertical" }} />
                </div>

              </form>
                </div>
              </div>
            </div>
          )}

          {/* ── Status Filters ── */}
          <div className="filter-bar" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            {[["", "All"], ["draft", "Draft"], ["sent", "Sent"], ["paid", "Paid"], ["adjusted", "Adjusted"], ["voided", "Voided"]].map(([s, label]) => (
              <button key={s || "all"} onClick={() => { setStatusFilter(s); setOverdueOnly(false); }} style={{
                padding: "6px 14px", borderRadius: 20, border: "1px solid #e2e8f0",
                background: statusFilter === s && !overdueOnly ? "#1e3a8a" : "white",
                color: statusFilter === s && !overdueOnly ? "white" : "#64748b",
                cursor: "pointer", fontSize: 13, fontWeight: 500,
              }}>{label}</button>
            ))}
            <button onClick={() => { setOverdueOnly(!overdueOnly); setStatusFilter(""); }} style={{
              padding: "6px 14px", borderRadius: 20, border: "1px solid #fca5a5",
              background: overdueOnly ? "#dc2626" : "white",
              color: overdueOnly ? "white" : "#dc2626",
              cursor: "pointer", fontSize: 13, fontWeight: 600,
            }}>⚠ Overdue</button>
          </div>

          {/* ── Invoice Table ── */}
          {isLoading && <p style={{ color: "#64748b" }}>Loading...</p>}
          <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
            <input
              value={invSearch} onChange={e => setInvSearch(e.target.value)}
              placeholder="Search invoice no. or customer…"
              style={{ ...inputStyle, maxWidth: 280, padding: "7px 12px" }}
            />
            {invSearch && <button onClick={() => setInvSearch("")} style={{ ...outlineBtnStyle, padding: "6px 10px", color: "#94a3b8" }}>✕</button>}
          </div>
          <div className="table-wrap" style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10 }}
               onClick={() => { setOpenMenuId(null); setMenuPos(null); }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  {([
                    { label: "Date", field: "issue_date" },
                    { label: "Invoice No.", field: "invoice_number" },
                    { label: "Customer / Project", field: "client_name" },
                    { label: "Grand Total", field: "total", alignRight: true },
                    { label: "Status", field: "status" },
                    { label: "", field: "" },
                  ] as { label: string; field: string; alignRight?: boolean }[]).map(h => (
                    <th key={h.label}
                      onClick={h.field ? () => setInvSort(s => ({ field: h.field, dir: s.field === h.field && s.dir === "asc" ? "desc" : "asc" })) : undefined}
                      style={{ padding: "10px 16px", textAlign: h.alignRight ? "right" : "left", fontWeight: 600, color: "#475569", fontSize: 12, whiteSpace: "nowrap", cursor: h.field ? "pointer" : "default", userSelect: "none" }}>
                      {h.label}{h.field ? (invSort.field === h.field ? (invSort.dir === "asc" ? " ↑" : " ↓") : " ↕") : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((inv: any) => {
                  const sc = STATUS_COLORS[inv.status] || STATUS_COLORS.draft;
                  const menuId = `inv-${inv.id}`;
                  return (
                    <tr key={inv.id} style={{ borderBottom: "1px solid #f1f5f9", background: inv.is_overdue ? "#fff7f7" : "white" }}
                        onMouseEnter={e => (e.currentTarget.style.background = inv.is_overdue ? "#fee2e2" : "#f8fafc")}
                        onMouseLeave={e => (e.currentTarget.style.background = inv.is_overdue ? "#fff7f7" : "white")}>
                      <td style={{ padding: "12px 16px", color: "#64748b", whiteSpace: "nowrap" }}>{formatDate(inv.issue_date)}</td>
                      <td style={{ padding: "12px 16px", fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap" }}>
                        <button onClick={() => startEditInvoice(inv)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "#1e3a8a", fontWeight: 600, fontSize: 13, textDecoration: "underline" }}>
                          {inv.invoice_number}
                        </button>
                        {inv.due_date && <div style={{ fontSize: 11, color: inv.is_overdue ? "#dc2626" : "#94a3b8", fontWeight: 400 }}>Due: {formatDate(inv.due_date)}{inv.is_overdue ? " ⚠" : ""}</div>}
                      </td>
                      <td style={{ padding: "12px 16px", color: "#1e293b" }}>{inv.client_name && inv.client_name !== "—" ? inv.client_name : <span style={{ color: "#94a3b8" }}>—</span>}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 600, color: "#1e3a8a", whiteSpace: "nowrap" }}>
                        {inv.currency_symbol}{inv.total?.toLocaleString("en", { minimumFractionDigits: 2 })} {inv.currency}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: "capitalize" }}>{inv.status}</span>
                      </td>
                      <td style={{ padding: "8px 16px", textAlign: "right", position: "relative", whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center" }}>
                          {(inv.status === "draft" || inv.status === "sent") && (
                            <button onClick={() => startEditInvoice(inv)} title="Edit" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>✏ Edit</button>
                          )}
                          <AsyncButton onClick={() => handleDownload(inv)} title="Download PDF" style={{ background: "#f0fdf4", border: "1px solid #86efac", color: "#166534", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                            {downloading === inv.id ? "…" : "⬇ PDF"}
                          </AsyncButton>
                          {inv.tds_amount > 0 && (
                            <AsyncButton onClick={() => handleDownloadWhtCertificate(inv)} title="Download WHT Certificate" style={{ background: "#fefce8", border: "1px solid #fde047", color: "#854d0e", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                              WHT Cert
                            </AsyncButton>
                          )}
                          <button onClick={e => { e.stopPropagation(); if (openMenuId === menuId) { setOpenMenuId(null); setMenuPos(null); } else { const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect(); setMenuPos({ top: r.bottom + 2, right: window.innerWidth - r.right }); setOpenMenuId(menuId); } }}
                            style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 10px", cursor: "pointer", color: "#64748b", fontSize: 16, lineHeight: 1 }}>⋯</button>
                          {openMenuId === menuId && menuPos && (
                            <div onClick={e => e.stopPropagation()} style={{ position: "fixed", top: menuPos.top, right: menuPos.right, zIndex: 999, background: "white", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: 160, padding: "4px 0" }}>
                              <button onClick={() => { duplicateMut.mutate(inv.id); setOpenMenuId(null); }} style={menuItemStyle}>⧉ Duplicate</button>
                              {inv.status === "draft" && (
                                <button onClick={() => { statusMut.mutate({ id: inv.id, status: "sent" }); setOpenMenuId(null); }} style={menuItemStyle}>✉ Mark Sent</button>
                              )}
                              {inv.status === "sent" && (
                                <button onClick={() => { statusMut.mutate({ id: inv.id, status: "paid" }); setOpenMenuId(null); }} style={menuItemStyle}>✓ Mark Paid</button>
                              )}
                              {inv.status !== "paid" && inv.status !== "voided" && inv.status !== "adjusted" && (
                                <AsyncButton onClick={async () => { if (await confirm(`Void invoice ${inv.invoice_number}? This cannot be undone.`, { confirmLabel: "Void" })) { voidMut.mutate(inv.id); setOpenMenuId(null); } }} style={{ ...menuItemStyle, color: "#dc2626" }}>✕ Void</AsyncButton>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!isLoading && filteredInvoices.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto", display: "block" }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                <div style={{ fontSize: 14, marginTop: 12, fontWeight: 600, color: "#64748b" }}>No invoices yet.</div>
                <button onClick={() => { setShowForm(true); setEditingInvoice(null); resetForm(); }} style={{ ...primaryBtnStyle, marginTop: 16 }}>+ New Invoice</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>

    {/* ── Create-product popup (triggered from line item) ── */}
    {productModal !== null && productModal.lineItemType !== null && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={() => setProductModal(null)}>
        <div style={{ background: "white", borderRadius: 12, padding: 28, width: 480, maxWidth: "90vw", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}
          onClick={e => e.stopPropagation()}>
          <h3 style={{ margin: "0 0 20px", fontSize: 17, color: "#0f172a", fontWeight: 700 }}>Save as Product</h3>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Product / Service Name *</label>
            <input value={productModal.name} onChange={e => setProductModal(m => m && ({ ...m, name: e.target.value }))}
              placeholder="e.g. DevOps, Web Design, Consulting"
              style={{ width: "100%", padding: "9px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 14, boxSizing: "border-box" }} autoFocus />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Description / Details</label>
            <textarea value={productModal.description} onChange={e => setProductModal(m => m && ({ ...m, description: e.target.value }))}
              placeholder="Describe the service or product in detail…" rows={3}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 14, resize: "vertical", boxSizing: "border-box" }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Default Unit Price</label>
            <input type="number" min={0} step="0.01" value={productModal.unit_price} onChange={e => setProductModal(m => m && ({ ...m, unit_price: e.target.value }))}
              placeholder="0.00"
              style={{ width: "100%", padding: "9px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 14, boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => setProductModal(null)} style={{ padding: "9px 18px", background: "none", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", fontSize: 14, color: "#64748b" }}>Cancel</button>
            <button disabled={createProductMut.isPending || !productModal.name.trim()} onClick={() => {
              createProductMut.mutate({ name: productModal.name.trim(), description: productModal.description.trim(), unit_price: parseFloat(productModal.unit_price) || 0 });
            }} style={{ padding: "9px 22px", background: "#1e3a8a", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
              {createProductMut.isPending ? "Saving…" : "Save Product"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  background: "linear-gradient(135deg, #1e3a8a, #00c9a7)",
  color: "white", border: "none", padding: "10px 20px",
  borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};

const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};

const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};

const errorTextStyle: React.CSSProperties = {
  color: "#dc2626", fontSize: 12, marginTop: 4,
};

const requiredMarkStyle: React.CSSProperties = {
  color: "#dc2626",
};

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};

const menuItemStyle: React.CSSProperties = {
  display: "block", width: "100%", textAlign: "left", padding: "8px 16px",
  background: "none", border: "none", cursor: "pointer", fontSize: 13,
  color: "#1e293b", whiteSpace: "nowrap",
};

function downloadCsv(filename: string, rows: any[][]) {
  const csv = rows.map(r => r.map((v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  // Leading BOM (bug-17) — without it, Excel opens this UTF-8 file as Windows-1252 and
  // renders any non-ASCII character (e.g. the em dash used for a blank field) as mojibake.
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function exportInvoicesCsv(invoices: any[]) {
  const headers = ["Invoice #", "Client", "Issue Date", "Due Date", "Total", "Currency", "Status"];
  const rows = invoices.map(inv => [
    inv.invoice_number || "",
    inv.client_name || "",
    inv.issue_date || "",
    inv.due_date || "",
    inv.total ?? "",
    inv.currency || "",
    (inv.status === "voided" ? "void" : inv.status) || "",
  ]);
  downloadCsv("invoices.csv", [headers, ...rows]);
}
